import type { HexMapState } from '../engine/render/renderer';
import type { Camera2D, Viewport } from '../engine/hex/camera2d';
import { axialToPixel } from '../engine/hex/hexMath';
import type { Rect, Tile } from '../engine/hex/types';
import {
    assignJob,
    recalculateRegionResources,
    syncJobsFromPeople,
} from '../engine/map/settlement';
import type { TutorialStep } from './steps/types';
import { buildColdSteps } from './steps/cold';
import { buildStarvationSteps } from './steps/starvation';
import { buildWeaponsSteps } from './steps/weapons';
import { buildWorkforceSteps } from './steps/workforce';
import { getActiveModal } from './steps/helpers';

export type TutorialState = {
    id: string;
    stepIndex: number;
    settlementId: string;
    settlementTileId: string;
    lockCamera: boolean;
    completed: boolean;
};

export function startTutorial(
    state: HexMapState,
    camera: Camera2D,
    viewport: Viewport,
    tutorialId: string,
    getTileById: (id: string) => Tile | null,
): void {
    const settlement = Object.values(state.settlementsById)[0];
    if (!settlement) return;
    const settlementTileId = settlement.tileId;
    state.tutorial = {
        id: tutorialId,
        stepIndex: 0,
        settlementId: settlement.id,
        settlementTileId,
        lockCamera: true,
        completed: false,
    };
    focusCameraOnSettlement(camera, settlementTileId, getTileById);
}

export function focusCameraOnSettlement(
    camera: Camera2D,
    settlementTileId: string,
    getTileById: (id: string) => Tile | null,
): void {
    const tile = getTileById(settlementTileId);
    if (!tile) return;
    const center = axialToPixel({ q: tile.position.q, r: tile.position.r });
    camera.zoom = 1.6;
    camera.offsetX = -center.x;
    camera.offsetY = -center.y;
}

export function updateTutorialProgress(state: HexMapState): void {
    if (!state.tutorial || state.tutorial.completed) return;
    const step = getTutorialStep(state);
    if (!step) return;
    if (!step.condition(state)) return;
    const nextIndex = state.tutorial.stepIndex + 1;
    const steps = getTutorialSteps(state);
    if (nextIndex >= steps.length) {
        state.tutorial.completed = true;
        state.tutorial.lockCamera = false;
        return;
    }
    state.tutorial.stepIndex = nextIndex;
}

export function getTutorialStep(state: HexMapState): TutorialStep | null {
    if (!state.tutorial) return null;
    const steps = getTutorialSteps(state);
    return steps[state.tutorial.stepIndex] || null;
}

export function getTutorialSteps(state: HexMapState): TutorialStep[] {
    const settlementId = state.tutorial?.settlementId || '';
    const settlement = settlementId ? state.settlementsById[settlementId] : null;
    const settlementTileId = state.tutorial?.settlementTileId || settlement?.tileId || '';
    const lang = state.config.ui.language;

    const context = { state, settlement, settlementId, settlementTileId, lang };
    if (state.tutorial?.id === 'weapons') return buildWeaponsSteps(context);
    if (state.tutorial?.id === 'starvation') return buildStarvationSteps(context);
    if (state.tutorial?.id === 'cold') return buildColdSteps(context);
    return buildWorkforceSteps(context);
}

export function applyTutorialScenario(
    tutorialId: string,
    state: HexMapState,
    settlementId: string,
    regionTiles: Tile[],
    mapTiles: Tile[] = regionTiles,
): void {
    if (
        tutorialId !== 'workforce' &&
        tutorialId !== 'starvation' &&
        tutorialId !== 'cold' &&
        tutorialId !== 'weapons'
    ) {
        return;
    }
    const settlement = state.settlementsById[settlementId];
    if (!settlement) return;
    regionTiles.forEach((tile) => {
        tile.biome.type = 'forest';
        tile.resources.trees = [{ age: 20, amount: 100 }];
        if (tutorialId === 'starvation') {
            tile.resources.wild_life = [
                { age: 5, amount: 12 },
                { age: 4, amount: 12 },
                { age: 3, amount: 12 },
            ];
        }
    });
    if (tutorialId === 'weapons') {
        settlement.people.forEach((person) => {
            person.job = null;
        });
        syncJobsFromPeople(settlement);
        assignJob(settlement, 'assistants');
        assignJob(settlement, 'assistants');
        assignJob(settlement, 'builders');
        assignJob(settlement, 'builders');
        const distance = 5;
        const candidates = mapTiles.filter(
            (tile) =>
                tile.id !== settlement.tileId &&
                tile.biome.type !== 'water' &&
                getAxialDistance(tile.position, settlement.tilePosition) === distance,
        );
        const exact = candidates.find(
            (tile) => getAxialDistance(tile.position, settlement.tilePosition) === distance,
        );
        const target =
            exact ||
            candidates.reduce<{ tile: Tile; distance: number } | null>((best, tile) => {
                const distance = getAxialDistance(tile.position, settlement.tilePosition);
                if (!best || distance > best.distance) {
                    return { tile, distance };
                }
                return best;
            }, null)?.tile ||
            regionTiles.find(
                (tile) => tile.id !== settlement.tileId && tile.biome.type !== 'water',
            ) ||
            regionTiles[0];
        if (target) {
            state.enemyForces = {};
            state.enemyForces[target.id] = {
                id: 'enemy_tutorial_1',
                tileId: target.id,
                faction: 'bandits',
                units: [{ type: 'bandits', count: 4 }],
                totalUnits: 4,
                createdAt: 0,
            };
        }
    }
    recalculateRegionResources(settlement, regionTiles);
}

function getAxialDistance(a: { q: number; r: number }, b: { q: number; r: number }): number {
    return Math.abs(a.q - b.q) + Math.abs(a.r - b.r);
}

export function getTutorialTargetRect(
    state: HexMapState,
    step: TutorialStep,
    camera: Camera2D,
    viewport: Viewport,
    getTileById: (id: string) => Tile | null,
): Rect | null {
    if (step.target.type === 'tile') {
        const tile = getTileById(step.target.tileId);
        if (!tile) return null;
        const world = axialToPixel({ q: tile.position.q, r: tile.position.r });
        const screen = camera.worldToScreen(world);
        const size = 80;
        return { x: screen.x - size / 2, y: screen.y - size / 2, w: size, h: size };
    }
    if (step.target.type === 'action') {
        const modal = getActiveModal(state);
        const actionId = (step.target as { actionId: string }).actionId;
        if (modal) {
            const action = modal.actionRects.find((rect) => rect.id === actionId);
            if (action) return action.rect;
        }
        const overlayAction = state.actionBarActions.find((rect) => rect.id === actionId);
        return overlayAction ? overlayAction.rect : null;
    }
    return null;
}

export function isTutorialActionAllowed(state: HexMapState, actionId: string): boolean {
    if (!state.tutorial || state.tutorial.completed) return true;
    const step = getTutorialStep(state);
    if (!step) return true;
    if (step.allowActionIds.length === 0) {
        logTutorialBlockedAction(step, actionId);
        return false;
    }
    const allowed = step.allowActionIds.includes(actionId);
    if (!allowed) {
        logTutorialBlockedAction(step, actionId);
    }
    return allowed;
}

function logTutorialBlockedAction(step: TutorialStep, actionId: string): void {
    const expected = step.allowActionIds.length > 0 ? step.allowActionIds.join(', ') : 'none';
    // eslint-disable-next-line no-console
    console.info(
        `[tutorial] blocked action "${actionId}" (step "${step.id}", expected: ${expected})`,
    );
}

export function isTutorialTileAllowed(state: HexMapState, tileId: string): boolean {
    if (!state.tutorial || state.tutorial.completed) return true;
    const step = getTutorialStep(state);
    if (!step) return true;
    if (step.target.type !== 'tile') return false;
    return step.target.tileId === tileId;
}
